"use client"

// Realtime connection manager: resilient broadcast subscriptions with auto-reconnect
// and lightweight keepalive when the tab is visible. Avoids heavy polling.

import { supabaseClientRealtime as supabase } from "@/utils/supabase/clientRealtime"

type BroadcastHandler = (room: string, payload: any) => void

type RoomState = {
  channel: ReturnType<typeof supabase.channel>
  backoffMs: number
  reconnectTimer: ReturnType<typeof setTimeout> | null
  onMessage: BroadcastHandler
}

class RealtimeManager {
  private rooms = new Map<string, RoomState>()
  private keepAliveTimer: ReturnType<typeof setInterval> | null = null
  private isStarted = false

  start() {
    if (this.isStarted) return
    this.isStarted = true
    // Visibility-driven reconnects and keepalive
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', this.onVisibilityChange)
    }
    this.setupKeepAlive()
  }

  stop() {
    if (!this.isStarted) return
    this.isStarted = false
    if (typeof document !== 'undefined') {
      document.removeEventListener('visibilitychange', this.onVisibilityChange)
    }
    this.teardownKeepAlive()
    // Remove all channels
    Array.from(this.rooms.values()).forEach(({ channel, reconnectTimer }) => {
      try { if (reconnectTimer) clearTimeout(reconnectTimer) } catch {}
      try { supabase.removeChannel(channel) } catch {}
    })
    this.rooms.clear()
  }

  subscribeToRooms(roomNames: string[], onMessage: BroadcastHandler) {
    this.start()
    const unique = Array.from(new Set(roomNames.map(this.canonicalizeRoom)))
    // Unsubscribe from rooms not in the new list
    Array.from(this.rooms.keys()).forEach((name) => {
      if (!unique.includes(name)) this.teardownRoom(name)
    })
    // Subscribe / ensure each room
    for (const name of unique) {
      const existing = this.rooms.get(name)
      if (!existing) {
        this.setupRoom(name, onMessage)
      } else {
        // Update handler to the latest provided by the caller
        existing.onMessage = onMessage
      }
    }

    return () => {
      // Caller-level cleanup: don't stop the whole manager (shared), just remove these rooms
      for (const name of unique) this.teardownRoom(name)
    }
  }

  forceReconnect() {
    // Recreate all channels immediately with their current handlers
    const snapshot = Array.from(this.rooms.entries())
    snapshot.forEach(([name, state]) => {
      try { if (state.reconnectTimer) clearTimeout(state.reconnectTimer) } catch {}
      try { supabase.removeChannel(state.channel) } catch {}
      // Preserve current backoff and handler, then re-setup
      this.rooms.delete(name)
      this.setupRoom(name, state.onMessage)
      const newState = this.rooms.get(name)
      if (newState) newState.backoffMs = state.backoffMs
    })
  }

  private setupRoom(name: string, onMessage: BroadcastHandler) {
    const channel = supabase
      .channel(name)
      .on('broadcast', { event: 'message' }, (payload: any) => {
        try {
          const current = this.rooms.get(name)
          // Always call the latest handler stored for this room
          ;(current?.onMessage || onMessage)?.(name, payload)
        } catch (e) { console.error(e) }
      })
      .subscribe((status) => {
        // Auto-reconnect with backoff on closures/timeouts/errors
        if (status === 'CLOSED' || status === 'TIMED_OUT' || status === 'CHANNEL_ERROR') {
          const state = this.rooms.get(name)
          if (!state) return
          if (state.reconnectTimer) clearTimeout(state.reconnectTimer)
          const delay = state.backoffMs
          state.reconnectTimer = setTimeout(() => {
            // Replace channel
            try { supabase.removeChannel(state.channel) } catch {}
            this.rooms.delete(name)
            // Exponential backoff up to 30s
            const nextBackoff = Math.min(delay * 2, 30000)
            this.setupRoom(name, state.onMessage)
            const newState = this.rooms.get(name)
            if (newState) newState.backoffMs = nextBackoff
          }, delay)
        }
        if (status === 'SUBSCRIBED') {
          const state = this.rooms.get(name)
          if (state) state.backoffMs = 1000 // reset backoff on success
        }
      })

    this.rooms.set(name, { channel, backoffMs: 1000, reconnectTimer: null, onMessage })
  }

  private teardownRoom(name: string) {
    const state = this.rooms.get(name)
    if (!state) return
    try { if (state.reconnectTimer) clearTimeout(state.reconnectTimer) } catch {}
    try { supabase.removeChannel(state.channel) } catch {}
    this.rooms.delete(name)
  }

  private onVisibilityChange = () => {
    if (document.visibilityState === 'visible') {
      // Force fast rejoin when tab becomes visible (covers browser timer throttling)
      this.forceReconnect()
    }
  }

  private setupKeepAlive() {
    if (this.keepAliveTimer) return
    // Send a lightweight ping on all channels every 25s when visible
    this.keepAliveTimer = setInterval(() => {
      if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return
      Array.from(this.rooms.values()).forEach(({ channel }) => {
        try {
          channel.send({ type: 'broadcast', event: 'ping', payload: { t: Date.now() } })
        } catch {}
      })
    }, 25000)
  }

  private teardownKeepAlive() {
    if (this.keepAliveTimer) {
      try { clearInterval(this.keepAliveTimer) } catch {}
      this.keepAliveTimer = null
    }
  }

  private canonicalizeRoom = (room: string) => room.includes(':') ? room.split(':').sort().join(':') : room
}

declare global {
  var __kraahlRealtimeManager: RealtimeManager | undefined
}

export const realtimeManager = globalThis.__kraahlRealtimeManager ?? (globalThis.__kraahlRealtimeManager = new RealtimeManager())
